"use client";

import { useTranslations } from "next-intl";
import {
  TemplateLoadingScreen as BuilderTemplateLoadingScreen,
  type TemplateLoadingApplication,
  type TemplateLoadingPhase,
  type TemplateLoadingScreenLabels,
} from "@/app/components/site-builder/editor";
import { WorkspaceAppIcon } from "@/app/components/workspace/apps/WorkspaceAppIcon";
import { TakeresWordmark } from "@/app/components/workspace/TakeresWordmark";
import {
  getWorkspaceApplication,
} from "@/app/components/workspace/apps/registry";

function TemplateLoadingApplicationLogo({
  application,
}: {
  application: TemplateLoadingApplication;
}) {
  const applicationConfig = getWorkspaceApplication(application);

  return (
    <span
      className={[
        "relative grid h-12 w-12 place-items-center overflow-hidden rounded-[17px] bg-gradient-to-br text-white shadow-sm",
        applicationConfig?.tone
          ?? "from-slate-500 via-slate-600 to-slate-700",
      ].join(" ")}
      data-template-loading-application-logo={application}
    >
      <span className="absolute inset-x-1.5 top-0 h-px bg-white/55" />
      <span className="scale-[0.72]">
        <WorkspaceAppIcon id={application} />
      </span>
    </span>
  );
}

export function TemplateLoadingScreen({
  active = true,
  application = "web",
  onComplete,
  phase = "opening",
}: {
  active?: boolean;
  application?: TemplateLoadingApplication;
  onComplete?: () => void;
  phase?: TemplateLoadingPhase;
}) {
  const t = useTranslations("components.templateLoadingScreen");
  const common = useTranslations("common");
  const labels: TemplateLoadingScreenLabels = {
    brand: common("brand"),
    phases: {
      applying: {
        description: t("applyingDescription"),
        title: t("applyingTitle"),
      },
      creating: {
        description: t("creatingDescription"),
        title: t("creatingTitle"),
      },
      opening: {
        description: t("openingDescription"),
        title: t("openingTitle"),
      },
      theming: {
        description: t("themingDescription"),
        title: t("themingTitle"),
      },
    },
    progress: t("progress"),
  };

  return (
    <BuilderTemplateLoadingScreen
      active={active}
      application={application}
      brandLogo={<TakeresWordmark label={common("brand")} size="sm" />}
      applicationLogo={(
        <TemplateLoadingApplicationLogo application={application} />
      )}
      labels={labels}
      onComplete={onComplete}
      phase={phase}
    />
  );
}
